import { Box, Stack, TextField, Typography } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1Button } from 'c1ui'
import { C1Dialog, C1DialogActions, C1DialogContent, C1DialogTitle } from 'components/core/dialog/C1Dialog'
import { TaskAPIError } from 'components/core/errors/task-api'
import { C1Link } from 'components/core/links/C1Link'
import { C1FormLabel } from 'components/core/RHKFormComponents/C1FormLabel'
import { SelectOne } from 'components/core/search'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { AppUserDisplayName } from 'components/pages/app/users/app-user-display-name'
import { C1EntitlementDisplayName } from 'components/pages/entitlement/display-name'
import { ResourceDisplayName } from 'components/pages/entitlement/resource'
import { useAlert } from 'hooks/useAlert'
import { AppEntitlementUserValue, AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { AppResourceValue } from 'lib/actions/apps/app-resources'
import { createRevokeTask, CreateRevokeTaskHandlerValues } from 'lib/actions/task'
import { UserContext } from 'lib/context/app'
import { initialTaskState, taskReducer } from 'lib/reducers/task'
import { routes } from 'lib/routes'
import { Status } from 'lib/rpc'
import { useClientRPC } from 'lib/rpcts'
import { AppEntitlement } from 'pbts/c1/api/app/v1/app_entitlement'
import { TaskServiceClient } from 'pbts/c1/api/task/v1/task.client'
import React, { ReactElement, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

export type RequestProps = {
    appUserId?: string,
    appUser?: AppEntitlementUserValue
    appEntitlement?: AppEntitlementValue
    appEntitlements?: AppEntitlement[]
    resource?: AppResourceValue
} & ClosableProps

export type SubmitCompleteProps = {
    submitComplete: boolean
}

const RevokeDialogTitle = ({ submitComplete }: SubmitCompleteProps): React.ReactElement => {
    if (submitComplete) {
        return <C1DialogTitle color="primary">
            <Box>
                <FormattedMessage
                    id="component_change_role_request_title"
                    defaultMessage="Your revoke request has been submitted"
                />
            </Box>
        </C1DialogTitle>
    }

    return <C1DialogTitle color="primary">
        <FormattedMessage id="component_change_role_request_title" defaultMessage="Revoke access" />
    </C1DialogTitle>
}

export type CompleteContentProps = {
    taskId: string
}

const RevokeCompleteDialogContent = ({ taskId }: CompleteContentProps): React.ReactElement => {
    return <Stack spacing={4} direction="column">
        <Box>
            <C1Link href={routes.task(taskId)}>
                <FormattedMessage id="component_view_request_text" defaultMessage="View request" />
            </C1Link>
        </Box>
        <Box>
            <FormattedMessage
                id="component_revoke_access_reason_input"
                defaultMessage="Your request was successfully submitted. Be on the lookout for notifications for updates on your request. You can also find more details in the ConductorOne app. "
            />
        </Box>
    </Stack>
}

export type RequestContentProps = {
    reasonInput: React.RefObject<HTMLInputElement>
    reasonError: boolean
    setReasonError: (b: boolean) => void
    reasonHelperText: string
    dropDown?: ReactElement
}

const RevokeRequestDialogContent = ({
    reasonInput,
    reasonError,
    setReasonError,
    reasonHelperText,
    dropDown,
}: RequestContentProps): React.ReactElement => {
    return <Stack spacing={4} direction="column">
        <Typography>
            <FormattedMessage
                id="component_revoke_access_details"
                defaultMessage="Submitting this request creates a revocation task, which might require review. We'll send you a notification when the access is revoked."
            />
        </Typography>
        {dropDown}
        <Stack spacing={2} direction="column">
            <Typography variant="h6" component="p">
                <FormattedMessage
                    id="component_revoke_access_reason_input"
                    defaultMessage="Why are you revoking access?"
                />
            </Typography>
            <TextField
                inputRef={reasonInput}
                multiline
                fullWidth
                rows={3}
                error={reasonError}
                onFocus={(): void => {
                    setReasonError(false)
                }}
                helperText={reasonHelperText}
            />
        </Stack>
    </Stack>
}

export type RevokeActionProps = {
    submitComplete: boolean
    submitDisabled: boolean
    onSubmitClick: () => void
    onCancelClick: () => void
}

const RevokeDialogButtons = ({
    submitComplete,
    submitDisabled,
    onSubmitClick,
    onCancelClick,
}: RevokeActionProps): React.ReactElement => {
    if (submitComplete) {
        return <C1Button onClick={onCancelClick} variant="outlined">
            <FormattedMessage id="component_revoke_access_cancel" defaultMessage="Make another request" />
        </C1Button>
    }

    return <Stack spacing={1} direction="row">
        <C1Button onClick={onCancelClick} variant="outlined">
            <FormattedMessage id="component_revoke_access_cancel" defaultMessage="Cancel" />
        </C1Button>
        <C1Button
            variant="contained"
            color="primary"
            type="submit"
            onClick={onSubmitClick}
            disabled={submitDisabled}
        >
            {submitDisabled ? (
                <FormattedMessage id="component_revoke_access_submit" defaultMessage="Revoking..." />
            ) : (
                <FormattedMessage id="component_revoke_access_submit" defaultMessage="Revoke" />
            )}
        </C1Button>
    </Stack>
}

export const RevokeGrantRequestDialog = ({
    close,
    appUserId,
    appUser,
    appEntitlement,
    appEntitlements,
    resource,
}: RequestProps): React.ReactElement | null => {
    const intl = useIntl()
    const alert = useAlert()
    const userContext = useContext(UserContext)
    const userId = userContext.user.id
    const [taskState, taskDispatch] = useReducer(taskReducer, initialTaskState)
    const taskSvc = useClientRPC(TaskServiceClient)
    const reasonInput = useRef<HTMLInputElement>(null)
    const [submitDisabled, setSubmitDisabled] = useState<boolean>(false)
    const [submitComplete, setSubmitComplete] = useState<boolean>(false)
    const [reasonError, setReasonError] = useState<boolean>(false)
    const [submitError, setSubmitError] = useState<RpcError | null>(null)
    const [taskId, setTaskId] = useState<string>('')
    const [filteredAppEntitlements, setFilteredAppEntitlements] = useState<
        AppEntitlement[] | AppEntitlementValue[] | undefined
    >(appEntitlements?.filter((ae) => !!ae.id))
    const [selectedAppEntitlement, setSelectedAppEntitlement] = useState<
        AppEntitlement | AppEntitlementValue | undefined
    >(appEntitlements ? appEntitlements[0] : appEntitlement)

    useEffect((): void => {
        const item = taskState.item
        if (item.status === Status.OK) {
            alert(
                intl.formatMessage({
                    id: 'pages_task_create_success',
                    defaultMessage: 'Request created',
                }),
                'success',
            )
            setSubmitDisabled(false)
            setSubmitComplete(true)
            setTaskId(item.item.id)
            setSubmitError(null)
            return
        }
        if (item.status === Status.ERROR) {
            setSubmitError(item.error)
            alert(
                intl.formatMessage({
                    id: 'pages_task_create_error',
                    defaultMessage: 'Request error',
                }),
                'error',
            )
            setSubmitDisabled(false)
            setSubmitComplete(false)
        }
    }, [taskState, alert, intl, setSubmitComplete, setSubmitDisabled, setSubmitError])

    useEffect((): void => {
        if (!filteredAppEntitlements && appEntitlements) {
            setFilteredAppEntitlements(appEntitlements.filter((ae) => ae.id !== ''))
        }
    }, [appEntitlements, filteredAppEntitlements, setFilteredAppEntitlements])

    const reasonHelperText = (): string => {
        if (reasonError) {
            return intl.formatMessage({
                id: 'component_access_request_reason_error',
                defaultMessage: 'Please enter a reason for this revoke request',
            })
        }
        return ''
    }

    const onCancelClick = (): void => {
        setReasonError(false)
        setSubmitDisabled(false)
        setSubmitComplete(false)
        setTaskId('')
        setSubmitError(null)
        close()
    }

    const onSubmitClick = async (): Promise<void> => {
        const description = reasonInput.current?.value
        if (!taskSvc || !selectedAppEntitlement || (!appUserId && !appUser?.id)) {
            setSubmitDisabled(false)
            setSubmitComplete(false)
            setTaskId('')
            return
        }
        if (!description) {
            setReasonError(true)
            setSubmitDisabled(false)
            setSubmitComplete(false)
            setTaskId('')
            return
        }
        const request: CreateRevokeTaskHandlerValues = {
            appID: selectedAppEntitlement.appId,
            appEntitlementID: selectedAppEntitlement.id,
            description,
            appUserId: appUserId || appUser?.id || '',
        }

        createRevokeTask(taskSvc, taskDispatch, request)
        setSubmitDisabled(true)
    }

    const dropDown = useMemo(() => (appEntitlements && filteredAppEntitlements
        ? <SelectOne<AppEntitlementValue>
            resultList={{ status: Status.OK, items: filteredAppEntitlements }}
            onQueryChange={(query): void => {
                setFilteredAppEntitlements(
                    appEntitlements.filter((element) => {
                        const displayName = element.slug || element.displayName
                        return displayName.toLowerCase().includes(query.toLowerCase()) && !!element.id && !element.isAutomationEnabled
                    }),
                )
            }}
            onSelected={(ae): void => {
                if (ae) {
                    const appEntitlementValue: AppEntitlementValue = {
                        ...ae,
                        app: resource?.app,
                        appResource: resource,
                        appResourceType: resource?.appResourceType,
                    }
                    setSelectedAppEntitlement(appEntitlementValue)
                }
            }}
            label={intl.formatMessage({
                id: 'revoke_dialog_entitlement_label',
                defaultMessage: 'What level of access do you need to revoke?',
            })}
            placeholder={intl.formatMessage({
                id: 'revoke_dialog_entitlement_select',
                defaultMessage: 'Basic member, admin, etc.',
            })}
            renderLabel={(option: AppEntitlement): string => option.slug || option.displayName || option.id}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: AppEntitlement): React.ReactElement => (
                <li {...props} key={option.id}>
                    <Typography noWrap>{option.slug || option.displayName}</Typography>
                </li>
            )}
        />
        : undefined
    ), [appEntitlements, filteredAppEntitlements, intl, resource])

    if ((!appUser && !appUserId)) {
        return null
    }

    return <>
        <C1Dialog open onClose={onCancelClick} fullWidth>
            <RevokeDialogTitle submitComplete={submitComplete} />
            <C1DialogContent>
                <Stack spacing={4} direction="column">
                    {submitError && <TaskAPIError
                        err={submitError}
                        title={
                            <FormattedMessage
                                id="component_task_create_error"
                                defaultMessage="Error: Failed to create task"
                            />
                        }
                    />
                    }
                    <Stack
                        spacing={4}
                        sx={{
                            border: '1px solid',
                            borderColor: 'border.primary',
                            borderRadius: 1,
                            p: 2,
                        }}
                    >
                        {appEntitlement && <Stack>
                            <C1FormLabel
                                label={
                                    <FormattedMessage defaultMessage="Entitlement" id="revoke_dialog_entitlement_label" />
                                }
                            />
                            <C1EntitlementDisplayName entitlement={appEntitlement} readOnly />
                        </Stack>}

                        {resource && <Stack>
                            <C1FormLabel
                                label={
                                    <FormattedMessage defaultMessage="Resource" id="revoke_dialog_resource_label" />
                                }
                            />
                            <ResourceDisplayName
                                resource={resource}
                                resourceType={resource.appResourceType}
                                appName={resource.app?.displayName}
                                readOnly
                            />
                        </Stack>
                        }

                        {appUser && appUser?.identityUser?.id !== userId && <Stack>
                            <C1FormLabel
                                label={
                                    <FormattedMessage defaultMessage="User" id="revoke_dialog_user_label" />
                                }
                            />
                            <AppUserDisplayName
                                appUser={appUser}
                                readOnly
                                includeDetails={false}
                            />
                        </Stack>}
                    </Stack>
                    {submitComplete
                        ? <RevokeCompleteDialogContent taskId={taskId} />
                        : <RevokeRequestDialogContent
                            reasonInput={reasonInput}
                            reasonError={reasonError}
                            setReasonError={setReasonError}
                            reasonHelperText={reasonHelperText()}
                            dropDown={dropDown}
                        />
                    }
                </Stack>
            </C1DialogContent>
            <C1DialogActions>
                <RevokeDialogButtons
                    submitComplete={submitComplete}
                    submitDisabled={submitDisabled}
                    onSubmitClick={onSubmitClick}
                    onCancelClick={onCancelClick}
                />
            </C1DialogActions>
        </C1Dialog>
    </>
}
